'use client';

import { type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';
import { HttpExecutionCard } from '../../../../artifacts/http/HttpExecutionCard';
import type { CardRenderContext } from '../../tool-config-types';
import { useHttpToolData } from '../../hooks/useHttpToolData';
import { getHITLLeadingHeaderActions } from '../shared/hitl-artifact-header-actions';
import { getGuardrailReasonHeaderAction } from '../shared/guardrail-reason-header-action';
import { formatArgsToHttpText } from '../../HITL/utils/http-formatters';

const HTTP_ERROR_SUMMARY_MAX_LENGTH = 200;

function summarizeHttpError({
  error,
  failedLabel,
  status,
  statusMessage,
}: {
  error?: string;
  failedLabel: string;
  status?: number;
  statusMessage?: string;
}): string | undefined {
  if (!error) {
    return undefined;
  }

  if (status !== undefined) {
    return statusMessage
      ? `${failedLabel} — ${status} ${statusMessage}`
      : `${failedLabel} — ${status}`;
  }

  const firstLine = error
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.length > 0);

  return firstLine
    ? firstLine.slice(0, HTTP_ERROR_SUMMARY_MAX_LENGTH)
    : undefined;
}

type HttpInlineArtifactBodyProps = {
  input: unknown;
  output: unknown;
  error?: string;
  headerActions?: ReactNode;
  isResponsePending?: boolean;
  defaultResponseExpanded?: boolean;
  defaultRequestExpanded?: boolean;
};

function HttpInlineArtifactBody({
  input,
  output,
  error,
  headerActions,
  isResponsePending,
  defaultResponseExpanded = false,
  defaultRequestExpanded = false,
}: HttpInlineArtifactBodyProps) {
  const t = useTranslations('designSystem.chat');
  const { httpRequestData, httpResponseData } = useHttpToolData({
    input,
    output,
  });
  const errorSummary = summarizeHttpError({
    error,
    failedLabel: t('failed'),
    status: httpResponseData?.status,
    statusMessage: httpResponseData?.statusMessage,
  });

  const normalizedResponseHeaders = httpResponseData?.headers
    ? normalizeHttpHeaders(httpResponseData.headers)
    : undefined;
  const responseHttpVersion = httpResponseData
    ? resolveEffectiveHttpVersion({
        httpVersion: undefined,
        headers: httpResponseData.headers,
      })
    : undefined;

  const requestPayload = httpRequestData
    ? formatArgsToHttpText({
        url: httpRequestData.url,
        method: httpRequestData.method,
        headers: httpRequestData.headers,
        body: httpRequestData.body,
      })
    : undefined;
  const responseHeaderText = Object.entries(normalizedResponseHeaders ?? {})
    .map(
      ([key, value]) =>
        `${key}: ${Array.isArray(value) ? value.join(', ') : value}`,
    )
    .join('\n');
  const responsePayload = !httpResponseData
    ? undefined
    : httpResponseData.status === undefined && !responseHeaderText
      ? httpResponseData.body
      : `${responseHttpVersion ?? 'HTTP/1.1'} ${httpResponseData.status ?? ''} ${httpResponseData.statusMessage ?? ''}\n${responseHeaderText}${httpResponseData.body ? `\n\n${httpResponseData.body}` : ''}`;

  if (!requestPayload && !responsePayload && !error && !isResponsePending) {
    return null;
  }

  return (
    <HttpExecutionCard
      requestPayload={requestPayload ?? undefined}
      responsePayload={responsePayload ?? undefined}
      error={errorSummary}
      headerActions={headerActions}
      afterReplayActions={getGuardrailReasonHeaderAction(input)}
      isResponsePending={isResponsePending}
      defaultRequestExpanded={defaultRequestExpanded}
      defaultResponseExpanded={
        defaultResponseExpanded ||
        (httpResponseData?.status === undefined &&
          Boolean(httpResponseData?.body))
      }
    />
  );
}

export function renderHttpCard(ctx: CardRenderContext): ReactNode {
  return (
    <HttpInlineArtifactBody
      input={ctx.toolCallData.input}
      output={ctx.toolCallData.output}
      error={ctx.toolCallData.error}
      headerActions={getHITLLeadingHeaderActions({
        hitlDecisionType: ctx.hitlDecisionType,
        headerActions: ctx.headerActions,
      })}
      isResponsePending={
        (ctx.toolCallData.status === 'started' ||
          ctx.toolCallData.status === 'in-progress') &&
        !ctx.toolCallData.error &&
        ctx.hitlDecisionType !== 'edit' &&
        !(ctx.headerActions && !ctx.hitlDecisionType)
      }
      defaultResponseExpanded={ctx.forceExpanded === true}
      defaultRequestExpanded={ctx.forceExpanded === true}
    />
  );
}
